import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { User } from '../services/api';
import toast from 'react-hot-toast';
import i18n from '../utils/i18n';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { API_BASE_URL } from '../config/api';
import './LanguageSelector.css';

interface LanguageSelectorProps {
  user?: User | null;
  isInline?: boolean; // For navbar display
  /** Flag tiles for settings / forms (not navbar) */
  flagsInForm?: boolean;
}

const supportedLanguages = [
  { code: 'en', name: 'English', flag: 'us' },
  { code: 'pt', name: 'Português', flag: 'br' }
];

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  user,
  isInline = true,
  flagsInForm = false,
}) => {
  const { t } = useSafeTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<string>('en');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  useEffect(() => {
    // Set initial language from i18n
    if (i18n.isInitialized) {
      setCurrentLanguage(i18n.language || 'en');
    } else {
      // Wait for i18n to initialize
      const handleInitialized = () => {
        setCurrentLanguage(i18n.language || 'en');
      };
      i18n.on('initialized', handleInitialized);
      return () => i18n.off('initialized', handleInitialized);
    }

    // Listen for language changes
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const handleLanguageClick = (languageCode: string) => {
    if (languageCode === currentLanguage) return;
    
    setSelectedLanguage(languageCode);
    setShowConfirmModal(true);
  };

  const confirmLanguageChange = async () => {
    const selectedLang = supportedLanguages.find(lang => lang.code === selectedLanguage);
    
    try {
      setIsChangingLanguage(true);
      
      // Change i18n language
      await i18n.changeLanguage(selectedLanguage);
      
      // Save to localStorage
      localStorage.setItem('quizter-language', selectedLanguage);
      
      // If user is logged in, update preference in backend
      if (user) {
        try {
          const token = localStorage.getItem('authToken');
          if (token) {
            const response = await fetch(`${API_BASE_URL}/user/language`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({ language: selectedLanguage })
            });
            
            if (!response.ok) {
              /* backend update failed */
            }
          }
        } catch {
          /* ignore */
        }
      }
      
      setCurrentLanguage(selectedLanguage);
      toast.success(t('language.languageChanged', 'Language changed successfully'));
      
      
    } catch (error) {
      toast.error(t('messages.errorOccurred', 'An error occurred'));
    } finally {
      setIsChangingLanguage(false);
      setShowConfirmModal(false);
      setSelectedLanguage('');
    }
  };

  const cancelLanguageChange = () => {
    setShowConfirmModal(false);
    setSelectedLanguage('');
    toast(t('language.languageNotChanged', 'Language change cancelled'), { icon: 'ℹ️' });
  };

  const selectedLanguageData = supportedLanguages.find(lang => lang.code === selectedLanguage);

  if (isInline || flagsInForm) {
    const flagsClassName = flagsInForm
      ? 'language-flags language-flags--settings d-flex align-items-center flex-wrap gap-2'
      : 'language-flags d-flex align-items-center me-3';

    return (
      <>
        <div
          className={flagsClassName}
          role="group"
          aria-label={t('user.languagePreference', 'Language Preference')}
        >
          {supportedLanguages.map((language) => (
            <button
              key={language.code}
              type="button"
              className={`language-flag-btn language-flag-tile${
                currentLanguage === language.code ? ' active' : ''
              }`}
              onClick={() => handleLanguageClick(language.code)}
              disabled={isChangingLanguage}
              title={t('common.changeLanguage', 'Change Language') + ` - ${language.name}`}
              aria-pressed={currentLanguage === language.code}
              aria-label={language.name}
            >
              <span className={`fi fi-${language.flag}`} />
            </button>
          ))}
        </div>

        <Modal show={showConfirmModal} onHide={cancelLanguageChange} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('common.changeLanguage', 'Change Language')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              {t('language.confirmChangeMessage', 'This will change the entire interface to {{language}}. Do you want to continue?')
                .replace('{{language}}', selectedLanguageData?.name || '')}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={cancelLanguageChange}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button variant="primary" onClick={confirmLanguageChange} disabled={isChangingLanguage}>
              {isChangingLanguage ? t('common.loading', 'Loading...') : t('common.confirm', 'Confirm')}
            </Button>
          </Modal.Footer>
        </Modal>
      </>
    );
  }

  // Dropdown fallback (legacy profile forms)
  return (
    <div className="language-selector-dropdown">
      <label htmlFor="language-select" className="form-label">
        {t('user.languagePreference', 'Language Preference')}
      </label>
      <select
        id="language-select"
        className="form-select"
        value={currentLanguage}
        onChange={(e) => handleLanguageClick(e.target.value)}
        disabled={isChangingLanguage}
      >
        {supportedLanguages.map((language) => (
          <option key={language.code} value={language.code}>
            {language.name}
          </option>
        ))}
      </select>
      
      <Modal show={showConfirmModal} onHide={cancelLanguageChange} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.changeLanguage', 'Change Language')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {t('language.confirmChangeMessage', 'This will change the entire interface to {{language}}. Do you want to continue?')
              .replace('{{language}}', selectedLanguageData?.name || '')}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelLanguageChange}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button 
            variant="primary" 
            onClick={confirmLanguageChange}
            disabled={isChangingLanguage}
          >
            {isChangingLanguage ? t('common.loading', 'Loading...') : t('common.confirm', 'Confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LanguageSelector; 